import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import * as api from '../api.ts';
import { BuildingWizard } from './building-wizard.tsx';
import {
  Button,
  Cell,
  Field,
  Finding,
  Metric,
  Metrics,
  PageTitle,
  Row,
  Section,
  Status,
  Table,
} from '../ui.tsx';

/**
 * Guided setup — the first-run flow for a new organisation
 * (`docs/progressive-disclosure.md` §"Phase 3", the setup-wizard spec).
 *
 * It branches on **org shape** first — a tenant on a shared floor, a whole
 * building, a school or campus, or a multi-site organisation — then names the
 * organisation, and then hands the **first building** to the shared
 * {@link BuildingWizard} (piece 2), the exact same component the Locations "Add
 * building" CTA opens. There is one building-setup experience, not two: the
 * wizard walks place → assembly points → areas → wardens → people, and the shape
 * only tailors the org-level copy around it.
 *
 * The shape and organisation steps are org-level and stay here; a separate later
 * piece reworks the whole org setup wizard, so this deliberately does no more
 * than route the building step through the shared component.
 *
 * It is **never a trap.** The rail stays visible the whole time, and there is a
 * plain way out to the dashboard on every step.
 */

type Shape = 'tenant' | 'building' | 'campus' | 'multi';

interface ShapeConfig {
  id: Shape;
  title: string;
  blurb: string;
  /** More than one site, so the site step becomes an add-more list. */
  multiSite: boolean;
  /** The word this shape uses for the space inside a site. */
  zoneWord: string;
  zoneWordPlural: string;
  /**
   * Presence counting on by default — a whole building signs people in; a
   * shared-floor tenant does not. Passed to the building wizard's create step
   * (`presence_enabled`, migration 0081), which decides the accountability
   * denominator and is not editable afterwards.
   */
  presenceDefault: boolean;
}

const SHAPES: Record<Shape, ShapeConfig> = {
  tenant: {
    id: 'tenant',
    title: 'A tenant on a shared floor',
    blurb:
      'You occupy a suite or a floor inside a building somebody else runs. Your site is your space; the building or venue manager handles the rest, and you escalate to them.',
    multiSite: false,
    zoneWord: 'Area',
    zoneWordPlural: 'Areas',
    presenceDefault: false,
  },
  building: {
    id: 'building',
    title: 'A whole building',
    blurb:
      'You run the whole building, over one or more storeys. Its floors are the areas a warden sweeps, and people assemble at street level.',
    multiSite: false,
    zoneWord: 'Floor',
    zoneWordPlural: 'Floors',
    presenceDefault: true,
  },
  campus: {
    id: 'campus',
    title: 'A school or campus',
    blurb:
      'You run a site made of several buildings, wings or blocks. Each is an area to sweep, roles skew to staff, and people muster at more than one assembly point.',
    multiSite: false,
    zoneWord: 'Wing',
    zoneWordPlural: 'Wings',
    presenceDefault: true,
  },
  multi: {
    id: 'multi',
    title: 'Multiple sites',
    blurb:
      'You run more than one site. Add each one, with its own areas and its own assembly points; presence is decided per site.',
    multiSite: true,
    zoneWord: 'Zone',
    zoneWordPlural: 'Zones',
    presenceDefault: true,
  },
};

type StepId = 'shape' | 'org' | 'building' | 'review';

function readShape(orgId: string): Shape | null {
  try {
    const v = localStorage.getItem(`evac.setup-shape.${orgId}`);
    return v === 'tenant' || v === 'building' || v === 'campus' || v === 'multi' ? v : null;
  } catch {
    return null;
  }
}

function writeShape(orgId: string, shape: Shape) {
  try {
    localStorage.setItem(`evac.setup-shape.${orgId}`, shape);
  } catch {
    // The choice still applies for this session; it just will not be remembered.
  }
}

export function Setup({
  orgId,
  orgName,
  onDone,
}: {
  orgId: string;
  orgName: string;
  /** Leave the wizard for the live dashboard. */
  onDone: () => void;
}) {
  const [shape, setShape] = useState<Shape | null>(() => readShape(orgId));
  const sites = useQuery({ queryKey: ['sites', orgId], queryFn: () => api.getSites(orgId) });
  const siteList = sites.data?.sites ?? [];

  const cfg = shape ? SHAPES[shape] : null;

  const steps: { id: StepId; label: string }[] = useMemo(() => {
    if (!cfg) return [{ id: 'shape', label: 'Shape' }];
    return [
      { id: 'shape', label: 'Shape' },
      { id: 'org', label: 'Organisation' },
      { id: 'building', label: cfg.multiSite ? 'First building' : 'Building' },
      { id: 'review', label: 'Review' },
    ];
  }, [cfg]);

  // Where to land on entry. Shape first if it has never been chosen; otherwise
  // the building step, where the shared wizard picks up. Every step reads live
  // data, so re-entering never starts over.
  const [current, setCurrent] = useState<StepId>(() => (readShape(orgId) ? 'building' : 'shape'));

  const choose = (s: Shape) => {
    setShape(s);
    writeShape(orgId, s);
    setCurrent('org');
  };

  const goToStep = (id: StepId) => setCurrent(id);
  const idx = steps.findIndex((s) => s.id === current);
  const next = () => {
    const n = steps[idx + 1];
    if (n) setCurrent(n.id);
    else onDone();
  };
  const back = () => {
    const p = steps[idx - 1];
    if (p) setCurrent(p.id);
  };

  return (
    <>
      <PageTitle sub="A few calm minutes now, so nothing is being figured out during an emergency.">
        Set up {orgName}
      </PageTitle>

      {cfg ? (
        <Stepper steps={steps} current={current} siteCount={siteList.length} onGo={goToStep} />
      ) : null}

      <div className="mt-8">
        {current === 'shape' ? <ShapeStep chosen={shape} onChoose={choose} /> : null}

        {current === 'org' && cfg ? (
          <OrgStep orgId={orgId} orgName={orgName} shape={cfg} onNext={next} onBack={back} />
        ) : null}

        {current === 'building' && cfg ? (
          // The first building runs the shared wizard (piece 2) — the same
          // component the Locations "Add building" CTA opens. `embedded`
          // suppresses its own page title, pre-creation Cancel and stepper
          // (setup owns all three); on completion it hands control back to
          // setup's review step rather than navigating away. `presenceDefault`
          // carries the chosen shape's counted-vs-roster default into creation,
          // so the review step's Presence column reflects it.
          <BuildingWizard
            orgId={orgId}
            embedded
            presenceDefault={cfg.presenceDefault}
            onComplete={() => next()}
          />
        ) : null}

        {current === 'review' && cfg ? (
          <ReviewStep
            orgId={orgId}
            orgName={orgName}
            shape={cfg}
            sites={siteList}
            onDone={onDone}
          />
        ) : null}
      </div>

      {/* Never a trap. The rail is always there, and so is this. */}
      <div className="mt-12 border-t border-[var(--line)] pt-5">
        <Button variant="quiet" onClick={onDone}>
          Skip setup and go to the dashboard
        </Button>
      </div>
    </>
  );
}

/* --------------------------------------------------------------- the stepper */

function Stepper({
  steps,
  current,
  siteCount,
  onGo,
}: {
  steps: { id: StepId; label: string }[];
  current: StepId;
  siteCount: number;
  onGo: (id: StepId) => void;
}) {
  // A step reads as done when the thing it creates exists. Only two of them
  // have a hard signal (shape is chosen if we are past it; a site exists);
  // the rest are soft, so they never claim "done" they cannot prove.
  const currentIdx = steps.findIndex((s) => s.id === current);
  const doneOf = (id: StepId, i: number): boolean => {
    if (i < currentIdx && (id === 'shape' || id === 'org')) return true;
    // The building step reads as done the moment a building exists — the one
    // hard signal in the flow (the shared wizard creates it at its own step 1).
    if (id === 'building') return siteCount > 0;
    return false;
  };

  return (
    <nav aria-label="Setup steps" className="flex flex-wrap gap-1.5">
      {steps.map((s, i) => {
        const active = s.id === current;
        const done = doneOf(s.id, i);
        // The dot carries state by colour alone, and it is aria-hidden — so the
        // completion of each step is invisible to a screen reader. Say it in
        // words: aria-current marks the current step, and a visually-hidden
        // status word carries "completed" / "not started" for the rest.
        const status = done ? 'completed' : active ? 'current' : 'not started';
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onGo(s.id)}
            aria-current={active ? 'step' : undefined}
            className={`inline-flex items-center gap-2 rounded-[var(--radius-1)] border px-3 py-1.5 text-[13px] ${
              active
                ? 'border-[var(--line-strong)] bg-[var(--paper-3)] font-medium text-[var(--ink)]'
                : 'border-[var(--line)] text-[var(--ink-2)] hover:bg-[var(--paper-3)]'
            }`}
          >
            <span
              aria-hidden
              className="inline-block h-2 w-2 shrink-0 rounded-full"
              style={{
                background: done ? 'var(--safe)' : active ? 'var(--accent)' : 'var(--line-strong)',
              }}
            />
            <span className="mono text-[11px] text-[var(--ink-3)]">{i + 1}</span>
            {s.label}
            <span className="sr-only"> — {status}</span>
          </button>
        );
      })}
    </nav>
  );
}

/** The two buttons every step ends with. */
function StepNav({
  onNext,
  onBack,
  nextLabel = 'Continue',
  nextDisabled,
  showBack = true,
}: {
  onNext: () => void;
  onBack?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  showBack?: boolean;
}) {
  return (
    <div className="mt-8 flex items-center gap-3">
      <Button variant="primary" disabled={nextDisabled} onClick={onNext}>
        {nextLabel}
      </Button>
      {showBack && onBack ? (
        <Button variant="quiet" onClick={onBack}>
          Back
        </Button>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------- step 1 */

function ShapeStep({ chosen, onChoose }: { chosen: Shape | null; onChoose: (s: Shape) => void }) {
  return (
    <Section title="What shape is your organisation?">
      <p className="mb-5 max-w-[640px] text-[14px] text-[var(--ink-2)]">
        This only tailors the defaults and the words — everything is buildings, areas and people
        underneath, and you can change any of it later. Pick the one that fits best.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {(Object.keys(SHAPES) as Shape[]).map((id) => {
          const s = SHAPES[id];
          const active = chosen === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onChoose(id)}
              aria-pressed={active}
              className={`flex flex-col items-start rounded-[var(--radius-2)] border p-4 text-left transition-colors ${
                active
                  ? 'border-[var(--line-strong)] bg-[var(--paper-3)]'
                  : 'border-[var(--line-2)] hover:bg-[var(--paper-3)]'
              }`}
            >
              <span className="text-[15px] font-[650]">{s.title}</span>
              <span className="mt-1.5 text-[13px] leading-[19px] text-[var(--ink-2)]">
                {s.blurb}
              </span>
            </button>
          );
        })}
      </div>
    </Section>
  );
}

/* ------------------------------------------------------------------- step 2 */

function OrgStep({
  orgId,
  orgName,
  shape,
  onNext,
  onBack,
}: {
  orgId: string;
  orgName: string;
  shape: ShapeConfig;
  onNext: () => void;
  onBack: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(orgName);
  const [note, setNote] = useState<string | null>(null);

  const rename = useMutation({
    mutationFn: () => api.updateOrg(orgId, { name: name.trim() }),
    onSuccess: () => {
      setNote('Saved.');
      void qc.invalidateQueries({ queryKey: ['me'] });
      void qc.invalidateQueries({ queryKey: ['org', orgId] });
    },
    onError: (e: Error) => setNote(e.message),
  });

  const changed = name.trim() !== orgName && name.trim().length > 0;

  return (
    <Section title="Your organisation">
      <p className="mb-5 max-w-[640px] text-[14px] text-[var(--ink-2)]">
        This is already created — it is the safety group people are invited into, and its name
        appears on every alert, report and screen. Change it here if it is not quite right.
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-[340px]">
          <Field label="Organisation name" value={name} onChange={setName} />
        </div>
        <Button
          variant="secondary"
          disabled={!changed || rename.isPending}
          onClick={() => rename.mutate()}
        >
          {rename.isPending ? 'Saving…' : 'Save name'}
        </Button>
      </div>

      {note ? (
        <div className="mt-3">
          <Finding tone="neutral">{note}</Finding>
        </div>
      ) : null}

      <p className="mt-5 text-[13px] text-[var(--ink-2)]">
        Set up as {shape.title.toLowerCase()}. Next,{' '}
        {shape.multiSite ? 'add your sites' : 'set up your site'}.
      </p>

      <StepNav onNext={onNext} onBack={onBack} />
    </Section>
  );
}

/* -------------------------------------------------------------------- review */

function ReviewStep({
  orgId,
  orgName,
  shape,
  sites,
  onDone,
}: {
  orgId: string;
  orgName: string;
  shape: ShapeConfig;
  sites: api.Site[];
  onDone: () => void;
}) {
  const roster = useQuery({ queryKey: ['roster', orgId], queryFn: () => api.getRoster(orgId) });
  const zoneQueries = useQueries({
    queries: sites.map((s) => ({
      queryKey: ['zones', orgId, s.id],
      queryFn: () => api.getZones(orgId, s.id),
    })),
  });

  const totalZones = zoneQueries.reduce((n, q) => n + (q.data?.zones.length ?? 0), 0);
  const counts = roster.data?.counts;

  return (
    <Section title="Ready to go">
      <p className="mb-6 max-w-[640px] text-[14px] text-[var(--ink-2)]">
        Here is what is set up for <strong>{orgName}</strong>. Nothing here is final — every part of
        it is editable from the dashboard, and you can come back to this whenever you want.
      </p>

      <Metrics>
        <Metric label={shape.multiSite ? 'Sites' : 'Site'} value={sites.length} />
        <Metric label={shape.zoneWordPlural} value={totalZones} />
        <Metric label="On the list" value={counts?.active ?? '—'} />
        <Metric
          label="Awaiting reply"
          value={counts?.pending ?? '—'}
          tone={counts && counts.pending > 0 ? 'attention' : 'neutral'}
        />
      </Metrics>

      <div className="mt-6">
        <Table head={['Site', 'Presence', shape.zoneWord + 's']}>
          {sites.map((s, i) => (
            <Row key={s.id}>
              <Cell wide>{s.name}</Cell>
              <Cell>
                {s.presence_enabled === false ? (
                  <Status tone="neutral">Membership</Status>
                ) : (
                  <Status tone="safe">Counted</Status>
                )}
              </Cell>
              <Cell numeric>{zoneQueries[i]?.data?.zones.length ?? 0}</Cell>
            </Row>
          ))}
        </Table>
      </div>

      {sites.length === 0 ? (
        <div className="mt-5">
          <Finding>
            No site has been added yet. You can still go to the dashboard, but nothing can be
            drilled until a building exists.
          </Finding>
        </div>
      ) : null}

      <p className="mt-6 max-w-[640px] text-[13px] text-[var(--ink-2)]">
        Next on the dashboard: write the all-clear message template, and record who holds the
        authority to order an evacuation — the two things that unlock declaring a real emergency,
        both on the Settings screen.
      </p>

      <div className="mt-6">
        <Button variant="primary" onClick={onDone}>
          Go to the dashboard
        </Button>
      </div>
    </Section>
  );
}
